import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' or 'phone'
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phone: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  const from = location.state?.from?.pathname || '/browse';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let result;
      if (isLogin) {
        // Login with email or phone
        if (loginMethod === 'phone') {
          // Use phone login API with credentials for cookies
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          const response = await fetch(`${API_URL}/api/auth/login/phone`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include', // Important: include cookies
            body: JSON.stringify({
              phone: formData.phone,
              password: formData.password
            })
          });
          
          if (response.ok) {
            // httpOnly cookie is set automatically by server
            result = { success: true };
          } else {
            const error = await response.json();
            result = { success: false, error: error.detail || 'Login failed' };
          }
        } else {
          result = await login(formData.email, formData.password);
        }
      } else {
        // Registration - can use phone or email
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const registrationData = {
          username: formData.username,
          display_name: formData.username,
          password: formData.password
        };
        
        // Add email or phone (at least one required)
        if (formData.email) {
          registrationData.email = formData.email;
        }
        if (formData.phone) {
          registrationData.phone_number = formData.phone; // Backend expects phone_number
        }
        
        // Validate at least one contact method
        if (!formData.email && !formData.phone) {
          toast({
            variant: 'destructive',
            title: 'Missing Information',
            description: 'Please provide either email or phone number',
          });
          setLoading(false);
          return;
        }
        
        const response = await fetch(`${API_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include', // Important: include cookies
          body: JSON.stringify(registrationData)
        });
        
        if (response.ok) {
          // httpOnly cookie is set automatically by server
          result = { success: true };
        } else {
          const error = await response.json();
          result = { success: false, error: error.detail || 'Registration failed' };
        }
      }

      if (result.success) {
        toast({
          title: isLogin ? 'Welcome back!' : 'Account created!',
          description: isLogin ? 'You have successfully logged in.' : 'Your account has been created successfully.',
        });
        
        // If registered with phone only, show prompt to add email
        if (!isLogin && formData.phone && !formData.email) {
          toast({
            title: 'Add Email Later',
            description: 'You can add your email in Account Settings for better security.',
          });
        }
        
        navigate(from, { replace: true });
      } else {
        // Show specific error message from backend
        toast({
          variant: 'destructive',
          title: isLogin ? 'Login Failed' : 'Registration Failed',
          description: result.error || 'Please check your information and try again.',
        });
      }
    } catch (error) {
      console.error('Auth error:', error);
      toast({
        variant: 'destructive',
        title: 'Connection Error',
        description: error.message || 'Unable to connect to server. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-purple-900/20 to-black flex items-center justify-center px-4">
      {/* Skip to form link for keyboard users */}
      <a 
        href="#auth-form" 
        className="skip-link"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('auth-form')?.focus();
        }}
      >
        Skip to {isLogin ? 'login' : 'registration'} form
      </a>
      
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDEyOCwgOTAsIDIxMywgMC4xKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20" aria-hidden="true" />
      
      <main className="relative w-full max-w-md" role="main">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" aria-label="Go to I Got Soul homepage">
            <h1 className="text-5xl font-black bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 to-purple-500 bg-clip-text text-transparent mb-2">
              I Got Soul
            </h1>
          </Link>
          <p className="text-gray-400 text-sm">Your platform for Black excellence</p>
        </div>

        {/* Form Card */}
        <div 
          className="bg-black/80 backdrop-blur-xl rounded-lg p-8 shadow-2xl border border-purple-500/20"
          role="region"
          aria-labelledby="auth-heading"
        >
          <h2 id="auth-heading" className="text-2xl font-bold text-white mb-6 text-center">
            {isLogin ? 'Sign In' : 'Create Account'}
          </h2>

          <form 
            id="auth-form" 
            onSubmit={handleSubmit} 
            className="space-y-4"
            aria-label={isLogin ? 'Sign in form' : 'Create account form'}
            tabIndex={-1}
          >
            {!isLogin && (
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-2">
                  Username
                </label>
                <Input
                  id="username"
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required={!isLogin}
                  className="w-full bg-gray-900 border-gray-700 text-white focus:border-purple-500 focus-visible:ring-2 focus-visible:ring-purple-500"
                  placeholder="Choose a username"
                  aria-describedby="username-hint"
                  autoComplete="username"
                />
                <span id="username-hint" className="sr-only">Enter your desired username</span>
              </div>
            )}

            {isLogin && (
              <div className="flex gap-2 mb-4" role="group" aria-label="Login method selection">
                <Button
                  type="button"
                  onClick={() => setLoginMethod('email')}
                  variant={loginMethod === 'email' ? 'default' : 'outline'}
                  className={`flex-1 focus-visible:ring-2 focus-visible:ring-purple-500 ${loginMethod === 'email' ? 'bg-purple-600' : 'border-gray-600'}`}
                  aria-pressed={loginMethod === 'email'}
                  aria-label="Sign in with email"
                >
                  Email
                </Button>
                <Button
                  type="button"
                  onClick={() => setLoginMethod('phone')}
                  variant={loginMethod === 'phone' ? 'default' : 'outline'}
                  className={`flex-1 focus-visible:ring-2 focus-visible:ring-purple-500 ${loginMethod === 'phone' ? 'bg-purple-600' : 'border-gray-600'}`}
                  aria-pressed={loginMethod === 'phone'}
                  aria-label="Sign in with phone"
                >
                  Phone
                </Button>
              </div>
            )}

            {isLogin && loginMethod === 'phone' ? (
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                  Phone Number
                </label>
                <Input
                  id="phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-900 border-gray-700 text-white focus:border-purple-500 focus-visible:ring-2 focus-visible:ring-purple-500"
                  placeholder="+1 234 567 8900"
                  autoComplete="tel"
                  aria-describedby="phone-hint"
                />
                <span id="phone-hint" className="sr-only">Enter your phone number with country code</span>
              </div>
            ) : isLogin ? (
              <div>
                <label htmlFor="login-email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email or Username
                </label>
                <Input
                  id="login-email"
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-900 border-gray-700 text-white focus:border-purple-500 focus-visible:ring-2 focus-visible:ring-purple-500"
                  placeholder="Enter email or username (e.g., Yahway)"
                  autoComplete="username"
                  aria-describedby="login-email-hint"
                />
                <span id="login-email-hint" className="sr-only">Enter your email address or username</span>
              </div>
            ) : (
              <>
                <div>
                  <label htmlFor="register-email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email {!isLogin && <span className="text-gray-500 text-xs">(Optional - can add later)</span>}
                  </label>
                  <Input
                    id="register-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required={isLogin}
                    className="w-full bg-gray-900 border-gray-700 text-white focus:border-purple-500 focus-visible:ring-2 focus-visible:ring-purple-500"
                    placeholder="Enter your email"
                    autoComplete="email"
                    aria-describedby="register-email-hint"
                  />
                  <span id="register-email-hint" className="sr-only">Enter your email address for account verification</span>
                </div>
                
                {!isLogin && (
                  <div>
                    <label htmlFor="register-phone" className="block text-sm font-medium text-gray-300 mb-2">
                      Phone Number <span className="text-gray-500 text-xs">(Optional - alternative to email)</span>
                    </label>
                    <Input
                      id="register-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full bg-gray-900 border-gray-700 text-white focus:border-purple-500 focus-visible:ring-2 focus-visible:ring-purple-500"
                      placeholder="+1 234 567 8900"
                      autoComplete="tel"
                    />
                  </div>
                )}
              </>
            )}

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <Input
                id="password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="w-full bg-gray-900 border-gray-700 text-white focus:border-purple-500 focus-visible:ring-2 focus-visible:ring-purple-500"
                placeholder="Enter your password"
                minLength={6}
                autoComplete={isLogin ? "current-password" : "new-password"}
                aria-describedby="password-hint"
              />
              <span id="password-hint" className="sr-only">
                {isLogin ? 'Enter your account password' : 'Create a password with at least 6 characters'}
              </span>
            </div>

            {!isLogin && (
              <div 
                className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mb-4"
                role="note"
                aria-live="polite"
              >
                <p className="text-xs text-blue-300">
                  <span aria-hidden="true">💡 </span>Provide at least one: email or phone number. You can add the other later in settings.
                </p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-3 rounded-lg transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
              aria-label={loading ? 'Please wait, processing...' : (isLogin ? 'Sign in to your account' : 'Create your account')}
              aria-busy={loading}
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
          </form>

          {isLogin && (
            <div className="mt-4 text-center">
              <Link 
                to="/forgot-password" 
                className="text-purple-400 hover:text-purple-300 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 rounded"
                aria-label="Forgot your password? Reset it here"
              >
                Forgot password?
              </Link>
            </div>
          )}

          <div className="mt-4 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-purple-400 hover:text-purple-300 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 rounded"
              aria-label={isLogin ? 'Switch to create a new account' : 'Switch to sign in with existing account'}
            >
              {isLogin
                ? "Don't have an account? Sign up"
                : 'Already have an account? Sign in'}
            </button>
          </div>

          <div className="mt-4 text-center">
            <Link 
              to="/" 
              className="text-gray-500 hover:text-gray-400 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 rounded"
              aria-label="Go back to home page"
            >
              <span aria-hidden="true">← </span>Back to home
            </Link>
          </div>
        </div>

        {/* Demo Credentials */}
        <div 
          className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg"
          role="note"
          aria-label="Demo information"
        >
          <p className="text-xs text-yellow-200 text-center">
            <strong>Demo:</strong> Register a new account or use existing credentials
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
